// src/services/FirebaseUserService.js
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInAnonymously,
  updateProfile,
  onAuthStateChanged,
  signOut
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from './firebase'; // Fixed import path

class FirebaseUserService {
  constructor() {
    this.currentUser = null;
    this.isInitialized = false;
  }

  // Generate a more reliable temporary email for phone-based registration
  generateTempEmail(phone) {
    // Remove any non-digit characters and ensure it's clean
    const cleanPhone = phone.replace(/\D/g, '');
    return `user${cleanPhone}@temp-rsphysio.com`;
  }

  // Create a stronger temporary password
  generateTempPassword() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  // Sign up new user with name and phone
  async signup(name, phone) {
    try {
      console.log('🔄 Starting signup process...', { name, phone });

      // Validate inputs
      if (!name || !phone) {
        throw new Error('Name and phone number are required');
      }

      // Clean the phone number
      const cleanPhone = phone.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        throw new Error('Please enter a valid 10-digit phone number');
      }

      // Check if user already exists with this phone number
      console.log('🔍 Checking for existing user...');
      const existingUser = await this.checkUserByPhone(cleanPhone);
      if (existingUser) {
        return {
          success: false,
          message: "A user with this phone number already exists. Please login instead."
        };
      }

      // Create temporary email and password for Firebase Auth
      const tempEmail = this.generateTempEmail(cleanPhone);
      const tempPassword = this.generateTempPassword();

      console.log('📧 Creating Firebase Auth user...', tempEmail);

      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, tempEmail, tempPassword);
      const firebaseUser = userCredential.user;

      console.log('✅ Firebase Auth user created:', firebaseUser.uid);

      // Update the user's display name
      await updateProfile(firebaseUser, {
        displayName: name.trim()
      });

      console.log('📝 Creating user document in Firestore...');

      // Create user document in Firestore
      const userData = {
        uid: firebaseUser.uid,
        name: name.trim(),
        phone: cleanPhone,
        email: '', // Will be updated later if provided
        tempEmail: tempEmail,
        tempPassword: tempPassword, // Store temporarily for login (in production, use proper auth)
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isPhoneVerified: false,
        sessions: []
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), userData);

      console.log('✅ User document created successfully');

      this.currentUser = { ...userData, id: firebaseUser.uid };

      return {
        success: true,
        message: `Welcome ${name}! Your account has been created successfully.`,
        user: this.currentUser
      };

    } catch (error) {
      console.error('❌ Signup error:', error);
      
      let errorMessage = 'An error occurred during signup. Please try again.';
      
      // Handle specific Firebase errors
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this information already exists.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please try again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format. Please try again.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many attempts. Please wait a moment and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  // Login user with phone number
  async login(phone) {
    try {
      console.log('🔄 Starting login process...', phone);

      // Clean the phone number
      const cleanPhone = phone.replace(/\D/g, '');
      
      // Find user by phone number
      console.log('🔍 Looking for user in database...');
      const userData = await this.checkUserByPhone(cleanPhone);
      
      if (!userData) {
        return {
          success: false,
          message: "No account found with this phone number. Please sign up first."
        };
      }

      console.log('👤 User found, attempting authentication...');

      // Sign in with the temporary email and stored password
      const tempEmail = userData.tempEmail;
      const tempPassword = userData.tempPassword;
      
      if (!tempEmail || !tempPassword) {
        throw new Error('Authentication credentials not found. Please contact support.');
      }

      const userCredential = await signInWithEmailAndPassword(auth, tempEmail, tempPassword);
      const firebaseUser = userCredential.user;
      
      console.log('✅ Authentication successful');

      // Update current user
      this.currentUser = { ...userData, id: firebaseUser.uid };

      // Update last login timestamp
      await updateDoc(doc(db, 'users', userData.uid), {
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        message: `Welcome back, ${userData.name}!`,
        user: this.currentUser
      };

    } catch (error) {
      console.error('❌ Login error:', error);
      
      let errorMessage = 'Login failed. Please check your phone number and try again.';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this phone number. Please sign up first.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Authentication failed. Please try signing up again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid account data. Please contact support.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  // Check if user exists by phone number
  async checkUserByPhone(phone) {
    try {
      console.log('🔍 Checking for user with phone:', phone);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('phone', '==', phone.toString()));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = { id: userDoc.id, ...userDoc.data() };
        console.log('✅ User found:', userData.name);
        return userData;
      }
      
      console.log('ℹ️ No user found with this phone number');
      return null;
    } catch (error) {
      console.error('❌ Error checking user by phone:', error);
      return null;
    }
  }

  // Update user email
  async updateUserEmail(email) {
    try {
      if (!this.currentUser) {
        throw new Error('No user is currently logged in');
      }

      console.log('📧 Updating user email...', email);

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        throw new Error('Please enter a valid email address');
      }

      // Update email in Firestore
      await updateDoc(doc(db, 'users', this.currentUser.uid), {
        email: email.trim(),
        updatedAt: serverTimestamp()
      });

      // Update local user data
      this.currentUser.email = email.trim();

      console.log('✅ Email updated successfully');

      return {
        success: true,
        message: 'Email updated successfully!'
      };

    } catch (error) {
      console.error('❌ Error updating email:', error);
      return {
        success: false,
        message: error.message || 'Failed to update email. Please try again.'
      };
    }
  }

  // Save session data
  async saveSessionData(sessionData, pathway) {
    try {
      if (!this.currentUser) {
        throw new Error('No user is currently logged in');
      }

      console.log('💾 Saving session data...', pathway);

      const sessionRecord = {
        sessionId: `session_${Date.now()}`,
        data: sessionData,
        pathway: pathway,
        completedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };

      // Update user document with new session
      const userRef = doc(db, 'users', this.currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const sessions = userData.sessions || [];
        sessions.push(sessionRecord);

        await updateDoc(userRef, {
          sessions: sessions,
          lastSessionAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        console.log('✅ Session data saved successfully');
      }

      return {
        success: true,
        message: 'Session data saved successfully!',
        sessionId: sessionRecord.sessionId
      };

    } catch (error) {
      console.error('❌ Error saving session data:', error);
      return {
        success: false,
        message: 'Failed to save session data.'
      };
    }
  }

  // Get user sessions
  async getUserSessions() {
    try {
      if (!this.currentUser) {
        return { success: false, sessions: [] };
      }

      const userRef = doc(db, 'users', this.currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          success: true,
          sessions: userData.sessions || []
        };
      }

      return { success: false, sessions: [] };

    } catch (error) {
      console.error('❌ Error getting user sessions:', error);
      return { success: false, sessions: [] };
    }
  }

  // Get current user data
  getCurrentUser() {
    return this.currentUser;
  }

  // Logout user
  async logout() {
    try {
      console.log('🚪 Logging out user...');
      await signOut(auth);
      this.currentUser = null;
      console.log('✅ User logged out successfully');
      return {
        success: true,
        message: 'Logged out successfully!'
      };
    } catch (error) {
      console.error('❌ Logout error:', error);
      return {
        success: false,
        message: 'Failed to logout. Please try again.'
      };
    }
  }

  // Initialize service (call this when app starts)
  async initialize() {
    try {
      console.log('🔧 Initializing Firebase User Service...');
      
      // Listen for auth state changes
      onAuthStateChanged(auth, async (firebaseUser) => {
        console.log('🔄 Auth state changed:', firebaseUser ? 'User signed in' : 'User signed out');
        
        if (firebaseUser) {
          // Try to get user data from Firestore
          try {
            const userRef = doc(db, 'users', firebaseUser.uid);
            const userDoc = await getDoc(userRef);
            
            if (userDoc.exists()) {
              this.currentUser = { id: userDoc.id, ...userDoc.data() };
              console.log('✅ User data loaded from Firestore');
            } else {
              console.log('⚠️ User document not found in Firestore');
            }
          } catch (error) {
            console.error('❌ Error loading user data:', error);
          }
        } else {
          this.currentUser = null;
        }
      });
      
      this.isInitialized = true;
      console.log('✅ Firebase User Service initialized successfully');
      
    } catch (error) {
      console.error('❌ Error initializing Firebase User Service:', error);
    }
  }

  // Check if service is ready
  isReady() {
    return this.isInitialized;
  }
}

// Create and export a singleton instance
const firebaseUserService = new FirebaseUserService();
export default firebaseUserService;